import type { ProblemDetails } from '@/types/api';

/**
 * Erro vindo da API, com o ProblemDetails preservado.
 *
 * O correlationId e exposto separadamente porque a interface o mostra ao
 * usuario quando precisa acionar o suporte (especificacao V3.1, item 7.11).
 */
export class ErroDeApi extends Error {
  readonly status: number;
  readonly problema: ProblemDetails;
  readonly correlationId: string | undefined;

  constructor(status: number, problema: ProblemDetails) {
    super(problema.detail ?? problema.title ?? `A API respondeu ${String(status)}.`);

    this.name = 'ErroDeApi';
    this.status = status;
    this.problema = problema;
    this.correlationId = problema.correlationId;
  }

  /** Tipo estavel do erro, usado para decidir tratamento na interface. */
  get tipo(): string {
    return this.problema.type ?? '';
  }

  /** Erros por campo, quando a falha e de validacao. */
  get errosPorCampo(): Record<string, string[]> {
    return this.problema.errors ?? {};
  }
}

/** Falha de rede, timeout ou cancelamento: a requisicao nao chegou a resposta. */
export class ErroDeRede extends Error {
  constructor(causa: unknown) {
    super('Nao conseguimos falar com o servidor. Verifique sua conexao e tente novamente.');

    this.name = 'ErroDeRede';
    this.cause = causa;
  }
}

/**
 * Texto para as respostas que o servidor nao descreve.
 *
 * As falhas de regra do proprio sistema sempre trazem `detail` em portugues. O que cai
 * aqui e o que o pipeline do ASP.NET gera sozinho, sem corpo: a autorizacao recusada
 * produz um ProblemDetails cujo unico texto e `title: "Forbidden"`, e era isso que
 * aparecia na tela — "Forbidden Codigo para o suporte: 3011e85d...".
 */
const porStatus: Record<number, string> = {
  401: 'Sua sessao expirou. Entre de novo para continuar.',
  403: 'Voce nao tem permissao para isso. Fale com um administrador se precisar dessa acao.',
  404: 'Nao encontramos o que voce pediu, ou ele nao esta no seu escopo de area.',
  409: 'A situacao mudou enquanto esta tela estava aberta. Recarregue para ver o estado atual.',
  429: 'Muitas tentativas em pouco tempo. Espere um instante e tente de novo.',
};

/**
 * Mensagem pronta para exibir ao usuario.
 *
 * Segue a identidade verbal do guia: primeira pessoa do plural, factual, sem
 * pontuacao repetida, sem tom alarmante, com uma saida oferecida.
 */
export function descreverErro(erro: unknown): string {
  if (erro instanceof ErroDeApi) {
    /*
      `detail` primeiro, porque e o texto que o servidor escreveu para este caso. O mapa
      por status vem ANTES de `title`: title sem detail e sempre o nome em ingles do
      status HTTP, que nao explica nada a quem esta usando a tela.
    */
    const base =
      erro.problema.detail ??
      porStatus[erro.status] ??
      erro.problema.title ??
      'Nao conseguimos concluir a operacao.';

    return erro.correlationId === undefined
      ? base
      : `${base} Codigo para o suporte: ${erro.correlationId}.`;
  }

  if (erro instanceof ErroDeRede) {
    return erro.message;
  }

  return 'Algo inesperado aconteceu. Tente novamente em instantes.';
}
